import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast as sonnerToast } from 'sonner';

export interface Notification {
  id: string;
  title: string;
  description?: string;
  type: 'success' | 'error' | 'info' | 'warning';
  timestamp: Date;
}

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
  /**
   * Art des Verlaufseintrags (K47). Ohne Angabe `info`: bis WP6 stand jeder
   * nicht-destruktive Toast als "success" im Verlauf, auch "Nichts zu
   * optimieren". Echte Erfolge geben `tone: 'success'` an, `variant:
   * 'destructive'` gewinnt immer und ergibt `error`.
   */
  tone?: 'success' | 'info' | 'warning';
  /**
   * Optionale Aktion im Toast (Sonner-Button), z.B. "Rueckgaengig" am
   * Ablehnen-Toast der Werkbank. `durationMs` verlaengert das Zeitfenster
   * fuer solche Toasts ueber die Sonner-Default (~4s) hinaus.
   */
  action?: { label: string; onClick: () => void };
  durationMs?: number;
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  clearNotifications: () => void;
  toast: (options: ToastOptions) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };
    setNotifications((prev) => [newNotification, ...prev]);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Wrapper around sonner toast that also saves to history
  const toast = useCallback((options: ToastOptions) => {
    const type = options.variant === 'destructive' ? 'error' : (options.tone ?? 'info');

    addNotification({
      title: options.title,
      description: options.description,
      type,
    });

    sonnerToast(options.title, {
      description: options.description,
      action: options.action,
      duration: options.durationMs,
    });
  }, [addNotification]);

  return (
    <NotificationContext.Provider value={{ notifications, addNotification, clearNotifications, toast }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
